// preact/debug will be replaced with public/src/emptyModule in production
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as preactDebug from "preact/debug";

import AppRouter, { type RouteMap } from "AppRouter";
import * as analytics from "analytics";
import * as appStorePurchases from "appStorePurchases";
import * as deviceDetect from "deviceDetect";
import * as googleAnalytics from "googleAnalytics";
import * as internalLinkHandler from "internalLinkHandler";
import $ from "jquery";
import * as mainLayout from "mainLayout";
import * as offlineNotice from "offlineNotice";
import * as pageTransitions from "pageTransitions";
import AccountPageView from "pages/AccountPageView";
import AccountRenewalPageView from "pages/AccountRenewalPageView";
import AdminAPILogsPage from "pages/AdminAPILogsPage";
import AdminBookFeedbackPage from "pages/AdminBookFeedbackPage";
import AdminBookPage from "pages/AdminBookPage";
import AdminChatsPage from "pages/AdminChatsPage";
import AdminCouponsPage from "pages/AdminCouponsPage";
import AdminPage from "pages/AdminPage";
import AdminQuadernoInboxPage from "pages/AdminQuadernoInboxPage";
import AdminRevenuePage from "pages/AdminRevenuePage";
import AdminUsersPage from "pages/AdminUsersPage";
import AppPremiumPageView from "pages/AppPremiumPageView";
import AppWelcomePageView from "pages/AppWelcomePageView";
import AttributionsPageView from "pages/AttributionsPageView";
import AudioUploadedPageView from "pages/AudioUploadedPageView";
import ChatListPageView from "pages/ChatListPageView";
import ChatPageView from "pages/ChatPageView";
import CheckoutPageView from "pages/CheckoutPageView";
import DashboardPageView from "pages/DashboardPageView";
import DiscoverPageView from "pages/DiscoverPageView";
import FlashcardsPageView from "pages/FlashcardsPageView";
import HeatMapProgressPageView from "pages/HeatMapProgressPageView";
import ImportWordsPageView from "pages/ImportWordsPageView";
import LanguagesPageView from "pages/LanguagesPageView";
import LearnPageView from "pages/LearnPageView";
import LibraryPageView from "pages/LibraryPageView";
import PracticePageView from "pages/PracticePageView";
import PreactPage from "pages/PreactPage";
import PublicShelfPageView from "pages/PublicShelfPageView";
import ReaderPageView from "pages/ReaderPageView";
import ReaderReviewPageView from "pages/ReaderReviewPageView";
import SettingsPageView from "pages/SettingsPageView";
import SignInPromptPageView from "pages/SignInPromptPageView";
import StoryBotPageView from "pages/StoryBotPageView";
import StyleGuidePageView from "pages/StyleGuidePageView";
import UpgradedPageView from "pages/UpgradedPageView";
import UploadPageView from "pages/UploadPageView";
import UserPageView from "pages/UserPageView";
import WebReaderPageView from "pages/WebReaderPageView";
import WebReaderSignInPageView from "pages/WebReaderSignInPageView";
import WebsitesPageView from "pages/WebsitesPageView";
import WordsPageView from "pages/WordsPageView";
import * as premiumUpsellModal from "premiumUpsellModal";
import readlangClient from "readlangClient";
import * as readlangLocalStorage from "readlangLocalStorage";
import * as languageMap from "shared/languageMap";
import * as sharedPage from "sharedPage";
import * as signupModal from "signupModal";
import UserStore from "stores/UserStore";
import stores from "stores/stores";
import * as textSize from "textSize";
import * as tts from "tts";
import * as urlUtils from "urlUtils";
import {
  markUserFetched,
  markUserStale,
  shouldFetchUser,
} from "userFetchThrottle";
import * as privateLibraryView from "views/PrivateLibraryView";
import StartPageView from "views/StartPageView";
import * as ttsVoicePicker from "views/TTSVoicePickerView";
import * as walkthrough from "walkthrough";
import * as webSearch from "webSearch";

// Undo jquery's setting of zoom=1 on iOS
if (navigator.userAgent.match(/(iPhone|iPod|iPad)/i)) {
  $(document).ready(() => {
    $(document.body).css("zoom", "");
    textSize.follow();
  });
}

// No Google Analytics in the iOS app, so its App Privacy answers declare no
// tracking.
if (!deviceDetect.isIOSApp()) {
  googleAnalytics.setup();
}

const setLoggedInEmail = () => {
  if (stores.user.state.value.email != null) {
    readlangLocalStorage.setItem(
      "loggedInEmail",
      stores.user.state.value.email
    );
  } else {
    readlangLocalStorage.removeItem("loggedInEmail");
  }
};

const syncUserId = () => {
  if (
    stores.user.id.value !== "local" &&
    stores.user.id.value !== window.READLANG.userID
  ) {
    // Set window.READLANG.userID to be the new user ID fetched from the server
    window.READLANG.userID = String(stores.user.id.value);
  }
};

stores.user.onChange((a) => a.email, setLoggedInEmail);
stores.user.onChange((a) => a._id, syncUserId);

readlangClient.setUserModel(stores.user);
readlangClient.setSignupModal(signupModal);
tts.setNeedSpeakingModeVoiceHook(ttsVoicePicker.showSpeakingModeVoicePicker);

const DEBUG_MODE = false;
if (DEBUG_MODE) {
  // This doesn't work on android browser (android 2.3.5)
  window.onerror = (error, url, line) => {
    alert(error + "\n\nURL: " + url + "\nline: " + line);
  };
}

const updateCurrentLanguage = (language) => {
  if (language && language !== stores.user.currentLearningLanguage.value) {
    markUserFetched();
    stores.user
      .save({ currentLearningLanguage: language })
      .catch(markUserStale);
  }
};

const renderFlashcardsPage = (options?) => {
  mainLayout.renderPage(
    new PreactPage({
      name: "flashcardsPage",
      // Full screen, as Practice's other sessions and Chat: left by its ✕
      showHeader: false,
      Child: FlashcardsPageView,
    }),
    options
  );
};

// Pages that need an account show the sign-in form in their place in local
// (trial) mode. We can't render the real page there — its data fetches 401
// and readlangClient.handleError would reload the page in a loop.
const renderLocalModeSignupPrompt = async (
  pageName: string,
  heading = "Sign in to practice your words"
) => {
  await mainLayout.renderPage(
    new PreactPage({
      name: pageName,
      showHeader: true,
      Child: SignInPromptPageView,
    }),
    { heading, skipOnboardingDialogs: true }
  );
};

const renderLibraryPage = async (options: {
  shelfID?: string;
  tag?: string;
}) => {
  if (await readlangClient.isLocalMode()) {
    await renderLocalModeSignupPrompt(
      "libraryPage",
      "Sign in to keep your own texts"
    );
    return;
  }
  mainLayout.renderPage(
    new PreactPage({
      name: "libraryPage",
      showHeader: true,
      Child: LibraryPageView,
      keepAlive: () => "library",
    }),
    options
  );
};

const renderPracticePage = async (options?) => {
  if (await readlangClient.isLocalMode()) {
    // As the Practice tab (/learn) shows it, rather than full screen
    await renderLocalModeSignupPrompt("learnPage");
    return;
  }
  mainLayout.renderPage(
    new PreactPage({
      name: "practicePage",
      showHeader: false,
      Child: PracticePageView as any,
    }),
    options
  );
};

// Preserve the ReaderPageView while moving within a book, but remount for a
// different book so session-specific controllers and pane state start fresh.
const readerPreactPage = new PreactPage({
  name: "readerPage",
  showHeader: false,
  Child: ReaderPageView,
  deferRenderedCallback: true,
  preserveOnRerender: true,
  preserveKey: (options) => options?.bookID,
});

const routeHandlers = {
  // called before each route to be executed...
  execute: async function (callback, args) {
    if (stores.user.isNew.value || shouldFetchUser()) {
      await stores.user.fetch();
      markUserFetched();
      syncUserId();

      // Stuff to do after fetching user
      stores.storedNotifications.fetchAndSetup();

      if (deviceDetect.isStandalone()) {
        walkthrough.setAchievement("standalone", true);
      }

      const couponToken = localStorage?.getItem("couponToken");

      if (couponToken && !(await readlangClient.isLocalMode())) {
        localStorage.removeItem("couponToken");

        readlangClient.request(
          "POST",
          `/api/coupon/redeemToken/${couponToken}`,
          { couponToken },
          () => {
            // Success
            window.location.pathname = "/upgraded";
          }
        );
      }

      callback.apply(this, args);
    } else {
      callback.apply(this, args);
    }
  },

  startPage: function () {
    mainLayout.renderPage(
      new PreactPage({
        name: "startPage",
        showHeader: false,
        Child: StartPageView,
      })
    );
  },

  startLanguage: function (language) {
    if (!languageMap.map[language]) {
      this._notFound();
      return;
    }
    mainLayout.renderPage(
      new PreactPage({
        name: "startPage",
        showHeader: false,
        Child: StartPageView,
      }),
      {
        learningLanguage: language,
      }
    );
  },
  rootPath: async function () {
    // Only the iOS app reaches "/" while signed out (the server sends browsers
    // the landing page). The app has no trial mode, so welcome them to sign in.
    if (deviceDetect.isIOSApp() && (await readlangClient.isLocalMode())) {
      mainLayout.renderPage(
        new PreactPage({
          name: "appWelcomePage",
          showHeader: false,
          Child: AppWelcomePageView,
        })
      );
      return;
    }
    this._notFound();
  },
  // A page shared to the iOS app, to open once the user is signed in
  browse: function () {
    sharedPage.keep(new URLSearchParams(window.location.search).get("url"));
    appRouter.navigate(pageTransitions.tabPaths.Home, {
      replace: true,
      trigger: true,
    });
  },
  previousLibraryOrDashboard: function () {
    appRouter.navigate(
      localStorage.getItem("libraryOrDashboardPath") || "/dashboard",
      { trigger: true, replace: true }
    );
  },
  noop: function () {},
  websites: function (language) {
    updateCurrentLanguage(language);
    appRouter.navigate(
      "/" + stores.user.currentLearningLanguage.value + "/links",
      { replace: true }
    );
    mainLayout.renderPage(
      new PreactPage({
        name: "websitesPage",
        showHeader: true,
        Child: WebsitesPageView,
      }),
      { skipOnboardingDialogs: true }
    );
  },
  privateLibraryQueue: function (language) {
    updateCurrentLanguage(language);
    // The shelf or tag last shown, so a My Texts kept there comes back as left
    appRouter.navigate(privateLibraryView.myTextsPath(), {
      trigger: true,
      replace: true,
    });
  },
  privateLibraryTag: function (language, tag) {
    if (!tag) {
      tag = language;
      language = undefined;
    }
    if (tag === "previous") {
      routeHandlers.privateLibraryQueue(language);
      return;
    }
    updateCurrentLanguage(language);
    appRouter.navigate(
      "/" + stores.user.currentLearningLanguage.value + "/myTexts/" + tag,
      { replace: true }
    );
    void renderLibraryPage({ tag });
  },
  privateLibraryShelf: function (language, shelfID) {
    if (!shelfID) {
      shelfID = language;
      language = undefined;
    }
    updateCurrentLanguage(language);
    appRouter.navigate(
      "/" +
        stores.user.currentLearningLanguage.value +
        "/myTexts/shelf/" +
        shelfID,
      { replace: true }
    );

    void renderLibraryPage({ shelfID });
  },
  publicLibrary: function (language) {
    updateCurrentLanguage(language);
    appRouter.navigate(
      "/" + stores.user.currentLearningLanguage.value + "/library",
      { replace: true }
    );
    mainLayout.renderPage(
      new PreactPage({
        name: "discoverPage",
        showHeader: true,
        Child: DiscoverPageView,
        keepAlive: () => "discover",
      }),
      { skipOnboardingDialogs: true }
    );
  },
  publicShelf: function (shelfID) {
    mainLayout.renderPage(
      new PreactPage({
        name: "publicShelfPage",
        showHeader: true,
        Child: PublicShelfPageView,
      }),
      {
        shelfID,
        skipOnboardingDialogs: true,
      }
    );
  },
  reader: function (bookID, positionType, position) {
    const options: any = {
      bookID: bookID,
      skipOnboardingDialogs: true,
    };
    if (positionType === "end") {
      options.endOfText = true;
    } else if (positionType === "from") {
      options.from = position;
    } else if (positionType === "to") {
      options.to = position;
    } else if (positionType === "scroll") {
      options.scroll = position || 0;
    }
    mainLayout.renderPage(readerPreactPage, options);
  },
  readerEnd: function (bookID) {
    routeHandlers.reader(bookID, "end", undefined);
  },
  readerReview: function (bookID, returnPosition, position) {
    mainLayout.renderPage(
      new PreactPage({
        name: "readerReview",
        showHeader: false,
        Child: ReaderReviewPageView,
      }),
      {
        bookID: bookID,
        position: parseInt(position, 10),
        returnPosition: parseInt(returnPosition, 10),
      }
    );
  },
  flashcards: async function () {
    if (await readlangClient.isLocalMode()) {
      await renderLocalModeSignupPrompt("flashcardsPage");
      return;
    }
    const chosenSource = urlUtils.getUrlVar("source");
    if (chosenSource) {
      renderFlashcardsPage({
        language: stores.user.currentLearningLanguage.value,
        chosenSource,
      });
    } else {
      renderFlashcardsPage();
    }
  },
  flashcardsFromWords: async function (language, userWordIDs) {
    if (await readlangClient.isLocalMode()) {
      await renderLocalModeSignupPrompt("flashcardsPage");
      return;
    }
    renderFlashcardsPage({
      language,
      userWordIDs,
    });
  },
  flashcardsFromBook: async function (language, chosenBookID) {
    if (await readlangClient.isLocalMode()) {
      await renderLocalModeSignupPrompt("flashcardsPage");
      return;
    }
    renderFlashcardsPage({
      language,
      chosenBookID,
    });
  },
  learn: async function () {
    if (await readlangClient.isLocalMode()) {
      await renderLocalModeSignupPrompt("learnPage");
      return;
    }
    if (stores.flashcardSession?.inProgress()) {
      // User is mid-session, redirect to flashcards instead
      appRouter.navigate("/flashcards", { trigger: true, replace: true });
      return;
    }

    mainLayout.renderPage(
      new PreactPage({
        name: "learnPage",
        showHeader: true,
        Child: LearnPageView,
      })
    );
  },
  practice: function () {
    const source = urlUtils.getUrlVar("source");
    void renderPracticePage(source ? { source } : undefined);
  },
  progress: async function () {
    if (await readlangClient.isLocalMode()) {
      await renderLocalModeSignupPrompt(
        "progressPage",
        "Sign in to see your progress"
      );
      return;
    }
    mainLayout.renderPage(
      new PreactPage({
        name: "progressPage",
        showHeader: true,
        Child: HeatMapProgressPageView as any,
      })
    );
  },
  practiceFromBook: function (bookID) {
    void renderPracticePage({ bookID });
  },
  practiceFromWords: function (userWordIDs) {
    void renderPracticePage({ userWordIDs: userWordIDs.split("|") });
  },
  login: async function () {
    await renderLocalModeSignupPrompt("loginPage", "Let's get started!");
  },
  words: async function () {
    if (await readlangClient.isLocalMode()) {
      await renderLocalModeSignupPrompt(
        "wordsPage",
        "Sign in to manage your words"
      );
      return;
    }
    mainLayout.renderPage(
      new PreactPage({
        name: "wordsPage",
        showHeader: true,
        Child: WordsPageView,
        keepAlive: () => "words",
      })
    );
  },
  dashboard: function (language) {
    updateCurrentLanguage(language);
    appRouter.navigate(
      "/" + stores.user.currentLearningLanguage.value + "/dashboard",
      { replace: true }
    );
    mainLayout.renderPage(
      new PreactPage({
        name: "dashboardPage",
        showHeader: true,
        Child: DashboardPageView,
        keepAlive: () => "dashboard",
      })
    );
  },
  currentUser: async function () {
    const user = new UserStore();
    await user.fetch();
    $("body").html("fetched user = " + JSON.stringify(user));
  },
  settings: async function () {
    if (await readlangClient.isLocalMode()) {
      await renderLocalModeSignupPrompt(
        "settingsPage",
        "Sign in to manage your settings"
      );
      return;
    }

    mainLayout.renderPage(
      new PreactPage({
        name: "settingsPage",
        showHeader: true,
        Child: SettingsPageView,
      })
    );
  },
  storyBot: async function () {
    mainLayout.renderPage(
      new PreactPage({
        name: "storyBot",
        showHeader: true,
        Child: StoryBotPageView,
      })
    );
  },
  adminPage: function () {
    if (!stores.user.isGod.value) {
      this._notFound();
      return;
    }

    mainLayout.renderPage(
      new PreactPage({
        name: "adminPage",
        showHeader: true,
        Child: AdminPage,
      })
    );
  },
  adminCouponsPage: function () {
    if (!stores.user.isGod.value) {
      this._notFound();
      return;
    }

    mainLayout.renderPage(
      new PreactPage({
        name: "adminCouponPage",
        showHeader: true,
        Child: AdminCouponsPage,
      })
    );
  },
  adminUsersPage: function () {
    if (!stores.user.isGod.value) {
      this._notFound();
      return;
    }

    mainLayout.renderPage(
      new PreactPage({
        name: "adminUsersPage",
        showHeader: true,
        Child: AdminUsersPage,
      })
    );
  },
  adminChatsPage: function () {
    if (!stores.user.isGod.value) {
      this._notFound();
      return;
    }

    mainLayout.renderPage(
      new PreactPage({
        name: "adminChatsPage",
        showHeader: true,
        Child: AdminChatsPage,
      })
    );
  },
  adminBookFeedbackPage: function () {
    if (!stores.user.isGod.value) {
      this._notFound();
      return;
    }

    mainLayout.renderPage(
      new PreactPage({
        name: "adminBookFeedbackPage",
        showHeader: true,
        Child: AdminBookFeedbackPage,
      })
    );
  },
  adminAPILogsPage: function () {
    if (!stores.user.isGod.value) {
      this._notFound();
      return;
    }

    mainLayout.renderPage(
      new PreactPage({
        name: "adminAPILogsPage",
        showHeader: true,
        Child: AdminAPILogsPage,
      })
    );
  },
  adminRevenuePage: function () {
    if (!stores.user.isGod.value) {
      this._notFound();
      return;
    }

    mainLayout.renderPage(
      new PreactPage({
        name: "adminRevenuePage",
        showHeader: true,
        Child: AdminRevenuePage,
      })
    );
  },
  adminQuadernoInboxPage: function () {
    if (!stores.user.isGod.value) {
      this._notFound();
      return;
    }

    mainLayout.renderPage(
      new PreactPage({
        name: "adminQuadernoInboxPage",
        showHeader: true,
        Child: AdminQuadernoInboxPage,
      })
    );
  },
  adminBookPage: function (bookID: string) {
    if (!stores.user.isGod.value) {
      this._notFound();
      return;
    }

    mainLayout.renderPage(
      new PreactPage({
        name: "adminBookPage",
        showHeader: true,
        Child: AdminBookPage,
      }),
      { bookID }
    );
  },
  chromeAppStart: function () {
    localStorage.setItem("referrer", "chromeApp");
    this._notFound();
  },
  notFound: function () {
    this._notFound();
  },
  _notFound: function () {
    appRouter.navigate("/dashboard", { replace: true, trigger: true });
  },
  styleGuide: function () {
    mainLayout.renderPage(
      new PreactPage({
        name: "styleGuidePage",
        showHeader: true,
        Child: StyleGuidePageView,
      })
    );
  },
  attributions: function () {
    mainLayout.renderPage(
      new PreactPage({
        name: "attributions",
        showHeader: true,
        Child: AttributionsPageView,
      })
    );
  },
  languages: function () {
    readlangClient.request("GET", "/api/languages", {}, (languagesData) => {
      mainLayout.renderPage(
        new PreactPage({
          name: "languages",
          showHeader: true,
          Child: LanguagesPageView,
        }),
        {
          languages: languagesData.languages.sort((left, right) =>
            left.name.localeCompare(right.name)
          ),
        }
      );
    });
  },
  checkout: function () {
    if (deviceDetect.isIOSApp()) {
      mainLayout.renderPage(
        new PreactPage({
          name: "appPremiumPage",
          showHeader: true,
          Child: AppPremiumPageView,
        })
      );
      return;
    }
    mainLayout.renderPage(
      new PreactPage({
        name: "checkout2Page",
        showHeader: false,
        Child: CheckoutPageView,
      })
    );
    analytics.trackEvent("openCheckout");
  },
  checkoutModalPreview: async function () {
    // Temporary localhost preview, including for accounts already on Plus.
    if (window.location.hostname !== "localhost") {
      this._notFound();
      return;
    }

    await mainLayout.renderPage(
      new PreactPage({
        name: "checkoutModalPreview",
        showHeader: true,
        Child: () => null,
      }),
      { skipOnboardingDialogs: true }
    );
    await premiumUpsellModal.show({
      heading: "Get unlimited phrase translations...",
      showTitle: false,
      plus: urlUtils.getUrlVar("plus") === "1",
    });
  },
  user: function (userID, language) {
    mainLayout.renderPage(
      new PreactPage({
        name: "userPage",
        showHeader: true,
        Child: UserPageView,
      }),
      { userID: userID, language: language }
    );
  },
  upgradedPage: function () {
    mainLayout.renderPage(
      new PreactPage({
        name: "upgradedPage",
        showHeader: true,
        Child: UpgradedPageView,
      })
    );
  },
  webReader: function () {
    // The app's browser runs the Web Reader on any site: nothing to install
    if (deviceDetect.isIOSApp()) {
      appRouter.navigate("/links", { trigger: true, replace: true });
      return;
    }
    mainLayout.renderPage(
      new PreactPage({
        name: "webReader",
        showHeader: true,
        Child: WebReaderPageView,
      })
    );
  },
  account: function () {
    mainLayout.renderPage(
      new PreactPage({
        name: "accountPage",
        showHeader: true,
        Child: AccountPageView,
      })
    );
  },
  accountRenew: function () {
    mainLayout.renderPage(
      new PreactPage({
        name: "accountPage",
        showHeader: true,
        Child: AccountRenewalPageView,
      })
    );
  },
  upload: function () {
    mainLayout.renderPage(
      new PreactPage({
        name: "uploadPage",
        showHeader: true,
        Child: UploadPageView,
      })
    );
  },
  audioUploaded: function () {
    mainLayout.renderPage(
      new PreactPage({
        name: "audioUploadedPage",
        showHeader: true,
        Child: AudioUploadedPageView,
      })
    );
  },
  webReaderSignIn: function () {
    console.log("web reader sign in page");
    mainLayout.renderPage(
      new PreactPage({
        name: "webReaderSignInPage",
        showHeader: true,
        Child: WebReaderSignInPageView,
      })
    );
  },
  importWords: function () {
    mainLayout.renderPage(
      new PreactPage({
        name: "importWordsPage",
        showHeader: true,
        Child: ImportWordsPageView,
      })
    );
  },
  chatStart: function () {
    const urlParams = new URLSearchParams(window.location.search);
    mainLayout.renderPage(
      new PreactPage({
        name: "chatPage",
        showHeader: false,
        Child: ChatPageView,
      }),
      {
        start: true,
        initialMessageID: urlParams.get("initialMessageID"),
      }
    );
  },
  chat: function (userChatID: string) {
    mainLayout.renderPage(
      new PreactPage({
        name: "chatPage",
        showHeader: false,
        Child: ChatPageView,
      }),
      { userChatID }
    );
  },
  chatList: function () {
    mainLayout.renderPage(
      new PreactPage({
        name: "chatListPage",
        showHeader: true,
        Child: ChatListPageView,
      })
    );
  },
};

const appRoutes = {
  "": routeHandlers.rootPath,
  flashcards: routeHandlers.flashcards,
  ":language/flashcards/book/:bookID": routeHandlers.flashcardsFromBook,
  ":language/flashcards/word/:userWordIDs": routeHandlers.flashcardsFromWords,

  // New practice session
  practice: routeHandlers.practice,
  "practice/book/:bookID": routeHandlers.practiceFromBook,
  "practice/words/:userWordIDs": routeHandlers.practiceFromWords,

  // Remove these after May 2024 - for backwards compatibility with old links. Change happened
  // on 1st March 2024
  ":language/learn/book/:bookID": routeHandlers.flashcardsFromBook,
  ":language/learn/word/:userWordIDs": routeHandlers.flashcardsFromWords,

  learn: routeHandlers.learn,

  dashboard: routeHandlers.dashboard,
  ":language/dashboard": routeHandlers.dashboard,

  library: routeHandlers.publicLibrary,
  ":language/library": routeHandlers.publicLibrary,
  "regenerate/:language/library": routeHandlers.publicLibrary,

  ":language/bookshelf": routeHandlers.privateLibraryQueue,
  ":language/bookshelf/:bookID": routeHandlers.privateLibraryQueue,

  myTexts: routeHandlers.privateLibraryQueue,
  ":language/myTexts": routeHandlers.privateLibraryQueue,

  "myTexts/:tag": routeHandlers.privateLibraryTag,
  ":language/myTexts/:tag": routeHandlers.privateLibraryTag,

  ":language/myTexts/shelf/:shelfID": routeHandlers.privateLibraryShelf,
  "myTexts/shelf/:shelfID": routeHandlers.privateLibraryShelf,

  "shelf/:shelfID": routeHandlers.publicShelf,
  "regenerate/shelf/:shelfID": routeHandlers.publicShelf,

  links: routeHandlers.websites,
  ":language/links": routeHandlers.websites,
  "regenerate/:language/links": routeHandlers.websites,

  "regenerate/library/:bookID": routeHandlers.reader,
  "library/:bookID": routeHandlers.reader,
  "library/:bookID/review/:returnPosition/:position":
    routeHandlers.readerReview,

  "library/:bookID/end": routeHandlers.readerEnd,
  "library/:bookID/:positionType": routeHandlers.reader,
  "library/:bookID/:positionType/:position": routeHandlers.reader,
  storyBot: routeHandlers.storyBot,

  importWords: routeHandlers.importWords,

  previousLibraryOrDashboard: routeHandlers.previousLibraryOrDashboard,
  browse: routeHandlers.browse,

  // These paths are standalone entrypoints or legacy modal routes. The app
  // historically matched them without rendering a page.
  "addDocument?:queryString": routeHandlers.noop,
  signup: routeHandlers.noop,
  test: routeHandlers.noop,

  login: routeHandlers.login,
  words: routeHandlers.words,
  user: routeHandlers.currentUser,
  attributions: routeHandlers.attributions,
  styleguide: routeHandlers.styleGuide,
  settings: routeHandlers.settings,
  admin: routeHandlers.adminPage,
  "admin/coupons": routeHandlers.adminCouponsPage,
  "admin/users": routeHandlers.adminUsersPage,
  "admin/chats": routeHandlers.adminChatsPage,
  "admin/bookFeedback": routeHandlers.adminBookFeedbackPage,
  "admin/apiLogs": routeHandlers.adminAPILogsPage,
  "admin/revenue": routeHandlers.adminRevenuePage,
  "admin/quadernoInbox": routeHandlers.adminQuadernoInboxPage,
  "admin/book/:bookID": routeHandlers.adminBookPage,
  account: routeHandlers.account,
  accountRenew: routeHandlers.accountRenew,
  "user/:userID": routeHandlers.user,
  "user/:userID/texts/:language": routeHandlers.user,
  "regenerate/user/:userID": routeHandlers.user,
  "regenerate/user/:userID/texts/:language": routeHandlers.user,
  languages: routeHandlers.languages,
  webReader: routeHandlers.webReader,
  webReaderSignIn: routeHandlers.webReaderSignIn,
  checkout: routeHandlers.checkout,
  "test/checkout-modal": routeHandlers.checkoutModalPreview,
  chat: routeHandlers.chatList,
  "chat/start": routeHandlers.chatStart,
  "chat/start/?:queryString": routeHandlers.chatStart,
  "chat/:userChatID": routeHandlers.chat,

  upload: routeHandlers.upload,
  audioUploaded: routeHandlers.audioUploaded,

  progress: routeHandlers.progress,

  upgraded: routeHandlers.upgradedPage,

  start: routeHandlers.startPage,
  "startLanguage/:language": routeHandlers.startLanguage,

  chromeAppStart: routeHandlers.chromeAppStart,
  "chromeAppStart/:language": routeHandlers.chromeAppStart,
  "*anything": routeHandlers.notFound,
} satisfies RouteMap;

export const appRouter = Object.assign(new AppRouter(appRoutes), routeHandlers);

if (deviceDetect.isIOSApp()) {
  window.READLANG.browserStart = webSearch.browserStart;
  appStorePurchases.listen();
}

readlangClient.fetchAuthenticatedState(() => {
  mainLayout.init(appRouter);

  appRouter.start();

  // Use router for all links, except if they have data-bypass attribute
  internalLinkHandler.setup($(document), appRouter);
  offlineNotice.follow();
});
